import type { Disposable } from "@/shared/contracts/lifecycle";
import {
  startFloatingTooltip,
  type FloatingTooltipCleanup,
  type FloatingTooltipOptions,
} from "@/shared/ui/floatingTooltip";

export type StickyTooltipSource = "hover" | "focus" | "sticky";

export interface StickyTooltipOpenRequest {
  readonly id: string;
  readonly reference: HTMLElement;
  readonly tooltip: HTMLElement;
  readonly source: StickyTooltipSource;
  readonly floatingOptions: FloatingTooltipOptions;
}

export interface StickyTooltipActiveSnapshot {
  readonly id: string;
  readonly sources: ReadonlySet<StickyTooltipSource>;
}

export interface StickyTooltipController extends Disposable {
  open(request: StickyTooltipOpenRequest): void;
  removeSource(id: string, source: StickyTooltipSource): void;
  close(): void;
  closeIfOutsidePointerTarget(target: EventTarget | null): void;
  getActiveSnapshot(): StickyTooltipActiveSnapshot | null;
}

interface ActiveTooltip {
  readonly id: string;
  readonly reference: HTMLElement;
  readonly tooltip: HTMLElement;
  readonly cleanup: FloatingTooltipCleanup;
  readonly sources: Set<StickyTooltipSource>;
}

export function createStickyTooltipController(): StickyTooltipController {
  let active: ActiveTooltip | null = null;

  const openActive = (request: StickyTooltipOpenRequest): void => {
    request.tooltip.hidden = false;
    request.tooltip.setAttribute("aria-hidden", "false");
    const cleanup = startFloatingTooltip(
      request.reference,
      request.tooltip,
      request.floatingOptions,
    );
    active = {
      id: request.id,
      reference: request.reference,
      tooltip: request.tooltip,
      cleanup,
      sources: new Set([request.source]),
    };
  };

  const closeActive = (): void => {
    if (active === null) return;
    const current = active;
    active = null;
    current.cleanup();
    current.tooltip.hidden = true;
    current.tooltip.setAttribute("aria-hidden", "true");
  };

  return {
    open(request: StickyTooltipOpenRequest): void {
      if (active !== null && active.id !== request.id) {
        closeActive();
      }
      if (active === null) {
        openActive(request);
        return;
      }
      active.sources.add(request.source);
    },
    removeSource(id: string, source: StickyTooltipSource): void {
      if (active === null || active.id !== id) return;
      active.sources.delete(source);
      if (active.sources.size === 0) {
        closeActive();
      }
    },
    close(): void {
      closeActive();
    },
    closeIfOutsidePointerTarget(target: EventTarget | null): void {
      if (active === null) return;
      if (!active.sources.has("sticky")) return;
      if (target === null) return;
      const node = target as Node;
      if (active.reference.contains(node)) return;
      if (active.tooltip.contains(node)) return;
      closeActive();
    },
    getActiveSnapshot(): StickyTooltipActiveSnapshot | null {
      if (active === null) return null;
      return {
        id: active.id,
        sources: new Set(active.sources),
      };
    },
    dispose(): void {
      closeActive();
    },
  };
}
